import { buildProcessUrl } from '@/utils';
import {
  SubmitFormParams,
  SubmitFormResults,
  FetchFormSpecsParams,
  FormData as YepFormData,
} from '@/types';

export const fetchFormSpecs = async ({
  hostUrl,
  headers,
  team,
  processId,
}: FetchFormSpecsParams) => {
  const response = await fetch(buildProcessUrl(hostUrl, team, processId), {
    cache: 'force-cache',
    ...(headers && { headers: headers }),
  });
  const formSpecs = await response.json();
  if (response.status === 404) {
    throw new Error(
      `Process form specs couldn't be loaded. Have you enabled the form? Review documentation at https://yepcode.io/docs/forms/`
    );
  }
  if (response.ok) {
    return formSpecs;
  }
  throw formSpecs;
};

const DATA_URL_PATTERN = /^data:([^;,]+)?(?:;name=([^;,]+))?(?:;base64)?,(.*)$/;

const parseDataUrl = (
  value: string
): { mime: string; name: string; blob: Blob } | null => {
  const match = DATA_URL_PATTERN.exec(value);
  if (!match) {
    return null;
  }
  const [, mime = 'application/octet-stream', name = 'file', encoded] = match;
  const isBase64 = value.includes(';base64,');
  const data = isBase64
    ? Uint8Array.from(atob(encoded), (c) => c.charCodeAt(0))
    : new TextEncoder().encode(decodeURIComponent(encoded));
  return { mime, name, blob: new Blob([data], { type: mime }) };
};

type ExtractedFile = { key: string; file: File };

const extractFilesFromFormData = (
  formData: YepFormData
): { sanitized: YepFormData; files: ExtractedFile[] } => {
  const sanitized: YepFormData = { ...formData };
  const files: ExtractedFile[] = [];
  for (const [key, value] of Object.entries(formData)) {
    if (value instanceof File) {
      files.push({ key, file: value });
      delete sanitized[key];
    } else if (typeof value === 'string' && value.startsWith('data:')) {
      const parsed = parseDataUrl(value);
      if (parsed) {
        files.push({
          key,
          file: new File([parsed.blob], parsed.name, { type: parsed.mime }),
        });
        delete sanitized[key];
      }
    }
  }
  return { sanitized, files };
};

export const submitForm = async ({
  hostUrl,
  headers,
  team,
  processId,
  async,
  formData,
}: SubmitFormParams): Promise<SubmitFormResults> => {
  const { sanitized, files } = extractFilesFromFormData(formData);
  const url = buildProcessUrl(hostUrl, team, processId);
  const asyncHeader = { 'Yep-Async': async ? 'true' : 'false' };

  let response: Response;
  if (files.length > 0) {
    const body = new globalThis.FormData();
    body.append('parameters', JSON.stringify(sanitized));
    for (const { key, file } of files) {
      body.append(key, file, file.name);
    }
    const multipartHeaders: Record<string, string> = {
      ...headers,
      ...asyncHeader,
    };
    delete multipartHeaders['Content-Type'];
    delete multipartHeaders['content-type'];
    response = await fetch(url, {
      headers: multipartHeaders,
      method: 'POST',
      body,
    });
  } else {
    response = await fetch(url, {
      headers: {
        ...headers,
        'Content-Type': 'application/json',
        ...asyncHeader,
      },
      method: 'POST',
      body: JSON.stringify(formData),
    });
  }

  let responseObject = {};
  try {
    const responseText = await response.text();
    if (responseText.length) {
      responseObject = JSON.parse(responseText);
    }
  } catch (error) {
    console.error(`Unexpected error parsing response`, error);
    throw error;
  }
  if (response.ok) {
    return responseObject;
  }
  throw responseObject;
};
